// One theme query owner. The separate attribution owner reuses the guarded
// loader session; the native search layer remains disabled.
(function () {
  'use strict';
  const config = window.florafyCustomSearch;
  if (!window.florafySearchLayerGuard || !config || !/^\/apps\/[a-zA-Z0-9/_-]+$/.test(config.path) || config.path.includes('..')) return;
  const field = document.getElementById('doofinder-input');
  const shell = document.getElementById('florafy-search-shell');
  if (!field || !shell) return;
  const byId = (name) => document.getElementById('florafy-search-' + name);
  const labels = JSON.parse(byId('labels').textContent);
  const grid = byId('grid');
  const filters = byId('filters');
  const dialog = byId('filter-dialog');
  const status = byId('status');
  const pageElements = [document.getElementById('main'), document.querySelector('footer')].filter(Boolean);
  const header = document.querySelector('.site-header');
  const visibility = new Map();
  const cache = new Map();
  const queue = [];
  const pending = new WeakSet();
  const failed = new WeakSet();
  const collapsedFilters = new Set();
  let running = 0;
  let generation = 0;
  let controller;
  let debounce;
  let state = { query: '', page: 1, sort: 'relevance', filters: {} };
  let capabilities = { sorts: ['relevance'], filters: [] };
  let savedScroll = null;
  let savedFocus = null;
  let priorFocus = null;
  let priorScroll = null;
  let editingHistory = false;
  let resultTracking = null;
  let resultQuery = "";
  let searchAttempt = null;
  const termFields = ['categories', 'material'];
  const urlTermFields = [...termFields, 'collections'];
  const rangeFields = ['price', 'height', 'potSize'];
  const termParam = (name) => name === 'categories' ? 'fs_category' : 'fs_' + name;
  const rangeLimit = (name) => name === 'price' ? 10000000 : 1000;
  const owned = ['q', 'fs_page', 'fs_sort', ...urlTermFields.map(termParam), ...rangeFields.flatMap((name) => ['fs_' + name + '_min', 'fs_' + name + '_max'])];

  function node(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }
  function boundedQuery(value) {
    return value.trim().slice(0, 200).split(/\s+/).slice(0, 10).join(' ');
  }
  function readState() {
    const params = new URLSearchParams(location.search);
    const next = { query: boundedQuery(params.get('q') || ''), page: 1, sort: 'relevance', filters: {} };
    const page = Number(params.get('fs_page'));
    if (Number.isInteger(page) && page >= 1 && page <= 40) next.page = page;
    if (['relevance', 'price-asc', 'price-desc', 'title-asc', 'title-desc'].includes(params.get('fs_sort'))) next.sort = params.get('fs_sort');
    for (const name of urlTermFields) {
      const values = [...new Set(params.getAll(termParam(name)).map((value) => value.trim()).filter((value) => value && value.length <= 100))].slice(0, 5);
      if (values.length) next.filters[name] = values;
    }
    for (const name of rangeFields) {
      const range = {};
      for (const bound of ['min', 'max']) {
        const raw = params.get('fs_' + name + '_' + bound);
        if (raw !== null && raw !== '' && Number.isFinite(Number(raw)) && Number(raw) >= 0 && Number(raw) <= rangeLimit(name)) range[bound] = Number(raw);
      }
      if (Object.keys(range).length && !(range.min > range.max)) next.filters[name] = range;
    }
    return next;
  }
  function writeState(replace = false) {
    const url = new URL(location.href);
    for (const key of owned) url.searchParams.delete(key);
    if (state.query) {
      url.searchParams.set('q', state.query);
      if (state.page > 1) url.searchParams.set('fs_page', state.page);
      if (state.sort !== 'relevance') url.searchParams.set('fs_sort', state.sort);
      for (const name of urlTermFields) for (const value of state.filters[name] || []) url.searchParams.append(termParam(name), value);
      for (const name of rangeFields) for (const bound of ['min', 'max']) {
        if (state.filters[name]?.[bound] !== undefined) url.searchParams.set('fs_' + name + '_' + bound, state.filters[name][bound]);
      }
    }
    if (url.href !== location.href) history[replace ? 'replaceState' : 'pushState'](history.state, '', url.href);
  }
  // The compact header's search icon says whether search is open.
  function setToggleState(expanded) {
    document.querySelectorAll('.js-sticky-search-toggle').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }
  function revealField() {
    document.dispatchEvent(new CustomEvent('site-header:close-menu'));
    header?.classList.add('site-header--searching');
    setToggleState(true);
    field.focus({ preventScroll: true });
    field.scrollIntoView({ block: 'nearest', behavior: 'instant' });
  }
  function open() {
    if (!shell.hidden) return;
    savedScroll = priorScroll ?? window.scrollY;
    savedFocus = priorFocus || (document.activeElement === field ? null : document.activeElement);
    for (const element of pageElements) { visibility.set(element, element.hidden); element.hidden = true; }
    shell.hidden = false;
    header?.classList.add('site-header--searching');
    setToggleState(true);
    window.scrollTo(0, 0);
  }
  function close(updateHistory = true) {
    window.florafySearchAttribution?.cancelSearch();
    clearTimeout(debounce);
    generation += 1;
    controller?.abort();
    queue.length = 0;
    if (dialog.open) dialog.close();
    field.value = '';
    byId('clear').hidden = true;
    shell.hidden = true;
    header?.classList.remove('site-header--searching');
    setToggleState(false);
    for (const [element, hidden] of visibility) element.hidden = hidden;
    visibility.clear();
    state = { query: '', page: 1, sort: 'relevance', filters: {} };
    if (updateHistory) writeState();
    const returnFocus = savedFocus || priorFocus;
    field.blur();
    if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
    window.scrollTo(0, savedScroll ?? priorScroll ?? window.scrollY);
    savedFocus = null;
    savedScroll = null;
    priorScroll = null;
    priorFocus = null;
    editingHistory = false;
  }
  function validate(data, requested) {
    if (!data || !Array.isArray(data.products) || data.products.length > 24 || !Number.isSafeInteger(data.total) || data.total < 0 || data.page !== requested.page) throw new Error('Invalid search response');
    const context = data.context;
    if (!context || context.locale !== config.locale || context.country !== config.country || context.currency !== config.currency || context.engineCurrency !== 'EUR' || !/^[a-f0-9]{32}$/.test(context.engineHash || '') || !['nl', 'de', 'en', 'es', 'fr'].includes(context.engineLocale)) throw new Error('Search context mismatch');
    if (!data.capabilities || !Array.isArray(data.capabilities.sorts) || data.capabilities.sorts.length > 5 || !data.capabilities.sorts.includes('relevance') || data.capabilities.sorts.some((value) => !['relevance', 'price-asc', 'price-desc', 'title-asc', 'title-desc'].includes(value)) || !Array.isArray(data.capabilities.filters) || data.capabilities.filters.length > 5 || data.capabilities.filters.some((value) => ![...termFields, ...rangeFields].includes(value))) throw new Error('Invalid capabilities');
    const ids = new Set();
    for (const item of data.products) {
      if (!item || typeof item.id !== 'string' || !/^\d{1,30}$/.test(item.id) || typeof item.dfid !== 'string' || !item.dfid || item.dfid.length > 200 || ids.has(item.dfid) || typeof item.handle !== 'string' || !/^[a-zA-Z0-9][a-zA-Z0-9-]{0,254}$/.test(item.handle) || typeof item.title !== 'string' || item.title.length > 500 || !Number.isSafeInteger(item.position) || item.position < (requested.page - 1) * 24 + 1 || item.position > requested.page * 24) throw new Error('Invalid search item');
      if (item.attribution !== undefined && (typeof item.attribution !== 'string' || item.attribution.length > 3000)) throw new Error('Invalid attribution');
      if (item.effectivePrice !== undefined && (!item.effectivePrice || !Number.isFinite(item.effectivePrice.amount) || item.effectivePrice.amount < 0 || item.effectivePrice.amount > 10000000 || item.effectivePrice.currency !== config.currency)) throw new Error('Invalid effective price');
      ids.add(item.dfid);
    }
    if (!Array.isArray(data.facets) || data.facets.length > 5) throw new Error('Invalid facets');
    const facetFields = new Set();
    for (const facet of data.facets) {
      if (!facet || facetFields.has(facet.field) || !data.capabilities.filters.includes(facet.field)) throw new Error('Invalid facet field');
      facetFields.add(facet.field);
      if (termFields.includes(facet.field)) {
        if (!Array.isArray(facet.values) || facet.values.length > 20 || facet.values.some((term) => !term || typeof term.value !== 'string' || !term.value.trim() || term.value.length > 100 || !Number.isSafeInteger(term.count) || term.count < 0)) throw new Error('Invalid category terms');
      } else if (!rangeFields.includes(facet.field) || !Number.isFinite(facet.min) || !Number.isFinite(facet.max) || facet.min < 0 || facet.max > rangeLimit(facet.field) || facet.min > facet.max) throw new Error('Invalid numeric facet');
    }
    if (data.total > 0 && data.capabilities.filters.includes('price') && !facetFields.has('price')) throw new Error('Price range unavailable');
    return data;
  }
  async function readJson(response) {
    if (!response.body) throw new Error('Search response missing');
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let size = 0;
    let text = '';
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        size += value.byteLength;
        if (size > 262144) throw new Error('Search response too large');
        text += decoder.decode(value, { stream: true });
      }
      return JSON.parse(text + decoder.decode());
    } finally { await reader.cancel().catch(() => {}); }
  }
  function productUrl(item) {
    const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const url = new URL(root + 'products/' + encodeURIComponent(item.handle), location.origin);
    url.searchParams.set('variant', item.id);
    return url;
  }
  function loadCard(item) {
    const url = productUrl(item);
    url.searchParams.set('view', 'search-card');
    const key = url.href;
    if (cache.has(key)) return cache.get(key);
    const promise = fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(8000) }).then((response) => {
      if (!response.ok) throw new Error('Search card unavailable');
      return response.text();
    });
    cache.set(key, promise);
    if (cache.size > 64) cache.delete(cache.keys().next().value);
    promise.catch(() => cache.delete(key));
    return promise;
  }
  function cardError(card) {
    card.classList.remove('is-loading');
    card.querySelector('.ui-skeleton')?.remove();
    card.setAttribute('aria-busy', 'false');
    card.append(node('p', 'ui-feedback', labels.error));
  }
  async function hydrate(card, item) {
    const html = await loadCard(item);
    if (!card.isConnected || !grid.contains(card)) return;
    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const canonical = fragment.querySelector('.block-product--v2');
    if (!canonical) throw new Error('Search card unavailable');
    const price = canonical.dataset.cardPrice;
    if (item.effectivePrice && (canonical.dataset.cardCurrency !== item.effectivePrice.currency || !/^\d+$/.test(price || '') || !Number.isSafeInteger(Number(price)) || Number(price) !== Math.round(item.effectivePrice.amount * 100))) throw new Error('Search price unverified');
    if (canonical.dataset.cardProductSelection === 'true') {
      if (canonical.dataset.productId !== item.id || !item.effectivePrice || canonical.querySelector('.js-block-add-to-cart, [data-variant-id]')) throw new Error('Search product unverified');
    } else if (canonical.dataset.cardVariantId !== item.id) throw new Error('Search variant unverified');
    // Keep the canonical link, wishlist, price and verified purchase mode. No
    // native subtree or click bridge participates. The attribution owner reads
    // this wrapper and observes the canonical cart owner's confirmed-add seam.
    card.replaceChildren(canonical);
    card.classList.remove('is-loading');
    card.setAttribute('aria-busy', 'false');
  }
  function drain() {
    while (running < 4 && queue.length) {
      const { card, item } = queue.shift();
      if (!card.isConnected || !grid.contains(card)) { pending.delete(card); continue; }
      running += 1;
      hydrate(card, item).catch(() => { if (card.isConnected) { failed.add(card); cardError(card); } }).finally(() => { pending.delete(card); running -= 1; updateCards(); });
    }
  }
  function updateCards() {
    for (const card of grid.children) {
      if (!card.classList.contains('is-loading') || pending.has(card) || failed.has(card)) continue;
      const bounds = card.getBoundingClientRect();
      if (bounds.top > innerHeight + 800 || bounds.bottom < -800 || queue.length >= 32) continue;
      pending.add(card);
      queue.push({ card, item: { id: card.dataset.searchId, handle: card.dataset.searchHandle, ...(card.dataset.searchPrice !== undefined ? { effectivePrice: { amount: Number(card.dataset.searchPrice), currency: config.currency } } : {}) } });
    }
    drain();
  }
  function renderCards(items) {
    queue.length = 0;
    grid.replaceChildren();
    for (const item of items) {
      const card = node('article', 'florafy-search-card is-loading');
      card.dataset.searchId = item.id;
      card.dataset.searchDfid = item.dfid;
      card.dataset.searchPosition = String(item.position);
      card.dataset.searchHandle = item.handle;
      if (item.effectivePrice) card.dataset.searchPrice = String(item.effectivePrice.amount);
      if (item.attribution && resultTracking) {
        card.dataset.searchAttribution = item.attribution;
        card.dataset.searchSession = resultTracking.sessionId;
        card.dataset.searchQuery = resultQuery;
        card.dataset.searchEngineHash = capabilities.engineHash;
        window.florafySearchAttribution.bind(card, resultTracking);
      }
      card.setAttribute('aria-busy', 'true');
      const skeleton = node('div', 'ui-skeleton');
      skeleton.setAttribute('aria-hidden', 'true');
      const link = node('a', 'ui-feedback', item.title);
      link.href = productUrl(item).href;
      card.append(skeleton, link);
      grid.append(card);
    }
    updateCards();
  }
  function renderFilters(data) {
    const focusKey = filters.contains(document.activeElement) ? document.activeElement.dataset.filterKey : null;
    capabilities = data.capabilities;
    filters.replaceChildren();
    for (const name of ['categories', 'price', 'height', 'material', 'potSize'].filter((name) => capabilities.filters.includes(name))) {
      const facet = data.facets.find((entry) => entry.field === name);
      if (rangeFields.includes(name) && !facet && !state.filters[name]) continue;
      const group = node('details', 'storefront-search__facet');
      group.dataset.filterGroup = name;
      group.open = !collapsedFilters.has(name);
      const summary = node('summary', '', labels[name === 'categories' ? 'category' : name]);
      summary.id = 'florafy-search-facet-' + name;
      group.setAttribute('aria-labelledby', summary.id);
      summary.dataset.filterKey = name + ':toggle';
      group.append(summary);
      group.addEventListener('toggle', () => { if (group.isConnected) { if (group.open) collapsedFilters.delete(name); else collapsedFilters.add(name); } });
      if (termFields.includes(name)) {
        const terms = data.facets.find((facet) => facet.field === name)?.values || [];
        const selected = state.filters[name] || [];
        // Keep chosen categories removable even if the current facet response
        // omits them; no fabricated count is shown for these selections.
        const values = [...terms];
        for (const value of selected) if (!values.some((term) => term.value === value)) values.push({ value });
        for (const term of values) {
          const label = node('label', 'ui-choice');
          const input = node('input');
          input.dataset.filterKey = name + ':' + term.value;
          input.dataset.filterField = name;
          input.type = 'checkbox'; input.value = term.value; input.checked = selected.includes(term.value);
          input.addEventListener('change', () => {
            const checked = [...filters.querySelectorAll('input[type="checkbox"]:checked')].filter((element) => element.dataset.filterField === name).map((element) => element.value);
            if (checked.length > 5) { input.checked = false; return; }
            state.filters[name] = checked;
            state.page = 1; writeState(); search();
          });
          label.append(input, node('span', '', term.value));
          if (term.count !== undefined) label.append(node('small', '', String(term.count)));
          group.append(label);
        }
      } else {
        const range = node('div', 'storefront-search__range');
        const title = labels[name];
        const format = name === 'price' ? new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency }) : new Intl.NumberFormat(config.locale);
        const formatted = (value) => format.format(value) + (name === 'price' ? '' : ' cm');
        const selected = state.filters[name] || {};
        const sliders = node('div', 'ui-range');
        const inputs = {};
        const outputs = {};
        // Control limits are not product facts or implicit selected filters.
        // Without metadata, leave room around the saved bounds for adjustment.
        const low = 0;
        const selectedHigh = Math.max(selected.min ?? 0, selected.max ?? 0);
        const fallbackHigh = Math.min(rangeLimit(name), Math.max(selectedHigh * 2, 1));
        const high = Math.max(facet?.max ?? fallbackHigh, selectedHigh, 1);
        let editingRange = false;
        function updateRange() {
          const value = state.filters[name] || {};
          const minimum = value.min ?? low;
          const maximum = value.max ?? high;
          for (const bound of ['min', 'max']) {
            const current = bound === 'min' ? minimum : maximum;
            const endpoint = bound === 'min' ? low : high;
            const knownEndpoint = facet && (bound === 'min' || endpoint === facet.max);
            outputs[bound].textContent = value[bound] !== undefined ? formatted(value[bound]) : knownEndpoint ? formatted(endpoint) : labels[bound === 'min' ? 'noMin' : 'noMax'];
            if (inputs[bound]) {
              inputs[bound].value = current;
              inputs[bound].setAttribute('aria-valuetext', value[bound] === undefined ? labels[bound === 'min' ? 'noMin' : 'noMax'] : formatted(value[bound]));
              inputs[bound].setAttribute(bound === 'min' ? 'aria-valuemax' : 'aria-valuemin', String(bound === 'min' ? maximum : minimum));
            }
          }
          const span = high - low;
          sliders.style.setProperty('--range-start', ((minimum - low) / span * 100) + '%');
          sliders.style.setProperty('--range-end', ((maximum - low) / span * 100) + '%');
        }
        const atControlPrecision = (value) => Math.round(value * 100) / 100;
        function changeRange(bound, candidate) {
          const value = atControlPrecision(candidate);
          const current = state.filters[name]?.[bound];
          // Preserve exact saved values until the handle moves at control precision.
          if (current !== undefined && atControlPrecision(current) === value) return;
          const next = { ...state.filters[name] };
          if (value === atControlPrecision(bound === 'min' ? low : high)) delete next[bound];
          else next[bound] = value;
          if (next.min > next.max) return;
          if (state.filters[name]?.[bound] === next[bound]) return;
          if (Object.keys(next).length) state.filters[name] = next;
          else delete state.filters[name];
          state.page = 1;
          writeState(editingRange);
          editingRange = true;
          controller?.abort(); generation += 1; clearTimeout(debounce);
          grid.replaceChildren(); queue.length = 0;
          status.textContent = labels.loading;
          byId('count').textContent = '';
          updateRange();
        }
        function finishRange() {
          if (!editingRange) return;
          editingRange = false;
          search();
        }
        for (const bound of ['min', 'max']) {
          const label = node('label', 'storefront-search__range-label');
          label.append(node('span', 'ui-visually-hidden', labels[bound]));
          const output = node('output');
          outputs[bound] = output;
          const inputId = 'florafy-search-' + name + '-' + bound;
          label.htmlFor = inputId;
          label.append(output); range.append(label);
          const input = node('input', 'ui-range__input');
          input.id = inputId;
          inputs[bound] = input;
          input.dataset.filterKey = name + ':' + bound + ':slider';
          input.type = 'range'; input.min = String(low); input.max = String(high); input.step = 'any';
          input.setAttribute('aria-label', title + ' ' + labels[bound]);
          input.addEventListener('input', () => {
            const other = state.filters[name]?.[bound === 'min' ? 'max' : 'min'] ?? (bound === 'min' ? high : low);
            const value = bound === 'min' ? Math.min(Number(input.value), other) : Math.max(Number(input.value), other);
            changeRange(bound, value);
          });
          input.addEventListener('keydown', (event) => {
            const step = name === 'price' ? Math.max(1, 10 ** Math.floor(Math.log10((high - low) / 100))) : 1;
            const increments = { ArrowLeft: -step, ArrowDown: -step, ArrowRight: step, ArrowUp: step, PageDown: -step * 10, PageUp: step * 10 };
            if (!Object.hasOwn(increments, event.key) && !['Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            const candidate = event.key === 'Home' ? low : event.key === 'End' ? high : Number(input.value) + increments[event.key];
            const other = Number(inputs[bound === 'min' ? 'max' : 'min'].value);
            const value = bound === 'min' ? Math.min(candidate, other) : Math.max(candidate, other);
            changeRange(bound, Math.max(low, Math.min(high, value)));
          });
          input.addEventListener('keyup', (event) => {
            if (['ArrowLeft', 'ArrowDown', 'ArrowRight', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End'].includes(event.key)) finishRange();
          });
          input.addEventListener('blur', finishRange);
          input.addEventListener('change', finishRange);
          sliders.append(input);
        }
        let pointer = null;
        const pointerValue = (event) => {
          const rect = sliders.getBoundingClientRect();
          return low + Math.max(0, Math.min(1, (event.clientX - rect.left - 12) / Math.max(1, rect.width - 24))) * (high - low);
        };
        const movePointer = (event) => {
          if (!pointer || event.pointerId !== pointer.id) return;
          const other = Number(inputs[pointer.bound === 'min' ? 'max' : 'min'].value);
          const value = pointer.bound === 'min' ? Math.min(pointerValue(event), other) : Math.max(pointerValue(event), other);
          changeRange(pointer.bound, value);
        };
        sliders.addEventListener('pointerdown', (event) => {
          if (!event.isPrimary || event.button !== 0) return;
          event.preventDefault();
          const value = pointerValue(event);
          const minimum = Number(inputs.min.value);
          const maximum = Number(inputs.max.value);
          const bound = Math.abs(value - minimum) < Math.abs(value - maximum) || (minimum === maximum && value <= minimum) ? 'min' : 'max';
          pointer = { id: event.pointerId, bound };
          inputs[bound].focus({ preventScroll: true });
          sliders.setPointerCapture(event.pointerId);
          movePointer(event);
        });
        sliders.addEventListener('pointermove', movePointer);
        for (const eventName of ['pointerup', 'pointercancel']) sliders.addEventListener(eventName, (event) => {
          if (!pointer || event.pointerId !== pointer.id) return;
          if (eventName === 'pointerup') movePointer(event);
          pointer = null;
          editingRange = false;
          sliders.releasePointerCapture(event.pointerId);
          search();
        });
        updateRange();
        group.append(sliders);
        group.append(range);
      }
      filters.append(group);
    }
    const reset = node('button', 'ui-button ui-button--link', labels.reset);
    reset.type = 'button';
    reset.addEventListener('click', () => { state.filters = {}; state.page = 1; writeState(); search(); });
    const hasFilters = capabilities.filters.length > 0 || Object.keys(state.filters).length > 0;
    if (hasFilters) filters.append(reset);
    byId('filter-toggle').hidden = !hasFilters;
    filters.hidden = !hasFilters;
    const select = byId('sort');
    select.replaceChildren(...capabilities.sorts.map((value) => { const option = node('option', '', labels[value]); option.value = value; return option; }));
    select.value = state.sort;
    byId('sort-label').hidden = capabilities.sorts.length < 2;
    if (focusKey) [...filters.querySelectorAll('[data-filter-key]')].find((input) => input.dataset.filterKey === focusKey)?.focus({ preventScroll: true });
  }
  async function search() {
    window.florafySearchAttribution?.cancelSearch();
    clearTimeout(debounce);
    controller?.abort();
    const current = ++generation;
    if (!state.query) { close(); return; }
    open();
    byId('clear').hidden = false;
    byId('heading').textContent = shell.dataset.headingTemplate.replace('__QUERY__', state.query);
    status.textContent = labels.loading;
    byId('count').textContent = '';
    byId('retry').hidden = true;
    byId('reset-error').hidden = true;
    byId('previous').hidden = true;
    byId('next').hidden = true;
    byId('page').textContent = '';
    grid.replaceChildren();
    queue.length = 0;
    filters.setAttribute('aria-busy', 'true');
    grid.setAttribute('aria-busy', 'true');
    const requested = JSON.parse(JSON.stringify(state));
    const owner = window.florafySearchAttribution;
    const tracking = await owner?.ready();
    if (current !== generation) return;
    const attemptKey = JSON.stringify(requested);
    if (tracking && searchAttempt?.failed && searchAttempt.key === attemptKey && searchAttempt.sessionId === tracking.sessionId) tracking.requestId = searchAttempt.requestId;
    searchAttempt = tracking ? {key:attemptKey, sessionId:tracking.sessionId, requestId:tracking.requestId, failed:true} : null;
    const requestController = new AbortController();
    controller = requestController;
    const timer = setTimeout(() => requestController.abort(), 10000);
    try {
      const response = await fetch(config.path, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, signal: controller.signal, body: JSON.stringify({ ...requested, ...(owner?.wire(tracking) ? {tracking:owner.wire(tracking)} : {}), locale: config.locale, country: config.country, currency: config.currency, ...(config.candidateLocationId ? { candidateLocationId: config.candidateLocationId } : {}) }) });
      const payload = await readJson(response);
      if (current !== generation) return;
      if (!response.ok) {
        const unavailable = ['search_not_configured', 'context_unavailable', 'unsupported_context'].includes(payload?.error);
        const unsupported = ['unsupported_filter', 'unsupported_sort'].includes(payload?.error);
        const error = new Error(unsupported ? labels.unsupported : unavailable ? labels.unavailable : labels.error);
        throw error;
      }
      const data = validate(payload, requested);
      if (searchAttempt) searchAttempt.failed = false;
      const confirmed = owner?.confirm(tracking, requested.query, data.attributionSession, data.context.engineHash);
      resultTracking = confirmed ? tracking : null;
      resultQuery = requested.query;
      filters.setAttribute('aria-busy', 'false');
      renderFilters(data);
      capabilities.engineHash = data.context.engineHash;
      status.textContent = data.total === 0 ? labels.empty : '';
      byId('count').textContent = data.total + ' ' + labels.products;
      byId('page').textContent = labels.page + ' ' + data.page;
      byId('previous').hidden = data.page <= 1;
      byId('next').hidden = data.page >= 40 || data.page * 24 >= data.total;
      renderCards(data.products);
    } catch (error) {
      if (current !== generation) return;
      status.textContent = [labels.unavailable, labels.unsupported].includes(error.message) ? error.message : labels.error;
      byId('retry').hidden = error.message === labels.unsupported;
      byId('reset-error').hidden = state.sort === 'relevance' && !Object.keys(state.filters).length;
      // An error never reuses stale counts, products or filter capabilities.
      filters.hidden = true;
      byId('filter-toggle').hidden = true;
      byId('sort-label').hidden = true;
    } finally {
      clearTimeout(timer);
      if (current === generation) { grid.setAttribute('aria-busy', 'false'); filters.setAttribute('aria-busy', 'false'); }
    }
  }
  function changeQuery(immediate) {
    const query = boundedQuery(field.value);
    // Typing keeps the field as typed; only submit normalises it. An edit that
    // leaves the normalised query unchanged (a space) starts nothing new.
    if (immediate) field.value = query; else if (query === state.query) return;
    searchAttempt = null;
    window.florafySearchAttribution?.cancelSearch();
    state = query === state.query ? { ...state, page: 1 } : { query, page: 1, sort: 'relevance', filters: {} };
    // Invalidate immediately, before the debounce, so a previous request
    // cannot paint results while the new input waits for its request.
    controller?.abort(); generation += 1; clearTimeout(debounce);
    writeState(editingHistory); editingHistory = true;
    if (!query) { close(); return; }
    open(); byId('clear').hidden = false;
    grid.replaceChildren(); queue.length = 0;
    status.textContent = labels.loading;
    byId('count').textContent = '';
    filters.hidden = true;
    byId('filter-toggle').hidden = true;
    byId('sort-label').hidden = true;
    if (immediate) search(); else debounce = setTimeout(search, 250);
  }
  // Capture before the canonical card's navigation handler. keepalive sends
  // once without holding navigation or interpreting a generic cart change.
  grid.addEventListener('click', (event) => {
    const card = event.target.closest('[data-search-attribution]');
    if (!card || event.target.closest('.js-block-add-to-cart, .js-product-wishlist-add, .js-product-wishlist-remove') || !event.target.closest('a, .block-product')) return;
    const owner = window.florafySearchAttribution;
    const link = event.target.closest('a') || card.querySelector('.stretched-link');
    owner?.send('click', owner.capture(card, link?.href));
  }, true);
  field.maxLength = 200;
  field.addEventListener('input', () => changeQuery(false));
  field.closest('form').addEventListener('submit', (event) => { event.preventDefault(); changeQuery(true); field.blur(); editingHistory = false; });
  field.addEventListener('focus', (event) => {
    if (shell.hidden && event.relatedTarget && event.relatedTarget !== field) priorFocus = event.relatedTarget;
    if (savedScroll === null && priorScroll === null) priorScroll = window.scrollY;
    header?.classList.add('site-header--searching');
    setToggleState(true);
  });
  field.addEventListener('blur', () => { editingHistory = false; if (shell.hidden) { header?.classList.remove('site-header--searching'); setToggleState(false); } });
  // A second tap on the search icon closes search. The field's blur fires
  // between pointerdown and click and already closes it, so remember at
  // pointerdown that search was open and let the click leave it closed.
  // Enter, Space and assistive technology fire no pointerdown; their click has
  // detail 0 and reads the open state directly.
  let toggleClosesSearch = false;
  document.addEventListener('pointerdown', (event) => {
    toggleClosesSearch = Boolean(event.target.closest('.js-sticky-search-toggle')) && Boolean(header?.classList.contains('site-header--searching'));
  }, true);
  setToggleState(false);
  document.addEventListener('focusin', (event) => { if (event.target !== field && shell.hidden) priorFocus = event.target; });
  byId('clear').addEventListener('click', () => { close(); revealField(); });
  byId('close').addEventListener('click', () => close());
  byId('retry').addEventListener('click', search);
  byId('reset-error').addEventListener('click', () => { state.sort = 'relevance'; state.filters = {}; state.page = 1; writeState(); search(); });
  byId('sort').addEventListener('change', (event) => { state.sort = event.target.value; state.page = 1; writeState(); search(); });
  for (const [name, increment] of [['previous', -1], ['next', 1]]) byId(name).addEventListener('click', () => { state.page += increment; writeState(); search(); shell.scrollIntoView({ block: 'start' }); });
  byId('filter-toggle').addEventListener('click', () => { byId('filter-slot').append(filters); dialog.showModal(); document.documentElement.classList.add('is-locked'); });
  for (const button of [byId('filter-apply'), dialog.querySelector('.js-search-filter-close')]) button.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
  dialog.addEventListener('close', () => { byId('results').prepend(filters); document.documentElement.classList.remove('is-locked'); byId('filter-toggle').focus({ preventScroll: true }); });
  window.matchMedia('(min-width: 768px)').addEventListener('change', (event) => { if (event.matches && dialog.open) dialog.close(); });
  document.addEventListener('click', (event) => { const closing = toggleClosesSearch || (event.detail === 0 && Boolean(header?.classList.contains('site-header--searching'))); toggleClosesSearch = false; if (event.target.closest('.js-sticky-search-toggle, .js-search-icon')) { event.preventDefault(); if (closing && event.target.closest('.js-sticky-search-toggle')) { if (!shell.hidden) close(); else { field.blur(); header?.classList.remove('site-header--searching'); setToggleState(false); } return; } priorFocus = event.target.closest('button') || event.target; priorScroll ??= window.scrollY; revealField(); } });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !dialog.open && (!shell.hidden || document.activeElement === field)) { event.preventDefault(); close(); }
    if ((event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey && String(event.key).toLowerCase() === 'k') { event.preventDefault(); priorFocus = document.activeElement; priorScroll ??= window.scrollY; revealField(); field.select(); }
  });
  const hint = document.querySelector('#doofinder-input ~ .ui-field__hint');
  if (hint) hint.textContent = /Mac|iPhone|iPad|iPod/.test(navigator.userAgentData?.platform || navigator.platform || '') ? '⌘K' : 'Ctrl K';
  let scrollFrame = 0;
  window.addEventListener('scroll', () => { if (!scrollFrame) scrollFrame = requestAnimationFrame(() => { scrollFrame = 0; updateCards(); }); }, { passive: true });
  window.addEventListener('popstate', () => { state = readState(); field.value = state.query; editingHistory = false; if (state.query) search(); else close(false); });
  state = readState();
  field.value = state.query;
  if (state.query) { writeState(true); search(); }
})();
